import globalStore from "/globalStore.js";
import { showUnreleasedReports } from "/reports2/report-definitions.js";


/**
 * The reports module's own pages open only for people whose seat (or permission override) grants
 * Reporting.ViewWithinPermissions, the same rule as its menu item (primary-navigation-setup.js reports2()). The API enforces it too. The Sales & CRM reports are not gated here; they have their own access.
 */
const canOpenReportsModule = () =>
{
	if (!globalStore.currentUser) return false;
	return globalStore.currentUser.hasFeatureAccess("Reporting.ViewWithinPermissions");
};


/**
 * Route guard for a released reports page: refuses anyone outside the menu's audience.
 */
const requireReportsModuleAccess = () =>
{
	if (!canOpenReportsModule()) return { path: "/" };
};


/**
 * Route guard for a page that isn't released (phase 2, or built ahead of design and API, running on mock data):
 * open only when `showUnreleasedReports` is switched on locally.
 */
const requireUnreleasedReportsShown = () =>
{
	if (!canOpenReportsModule()) return { path: "/" };
	if (!showUnreleasedReports) return { name: "reports2-index" };
};


/**
 * The Time engine route serves the five standard presets. The custom report builder ("new-custom") and saved custom
 * reports ("RPT-CUS-...") are phase 2 (spec §11) and no longer exist in the portal, so a link to one goes to the hub.
 * The page sets its own title from the report it loads (setPageTitle), so nothing is written to `meta` here.
 */
const requireTimeReport = (to) =>
{
	const reportId = String(to.params.reportId || "");
	const isCustom = reportId === "new-custom" || reportId.startsWith("RPT-CUS-");

	return isCustom ? { name: "reports2-index" } : requireReportsModuleAccess();
};

const routes = [


	//---- Reports (new reporting module — reports2)
	//	Report definitions/presets live in /reports2/report-definitions.js.
	//	All new report paths start with /reports2/.
	{
		path: '/reports2',
		name: "reports2-index",
		beforeEnter: requireReportsModuleAccess,
		component: () => import("/reports2/index.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "", pageTitle: "Reports" }
	},

	//---- Time engine (the five standard presets, saved custom reports, and the
	//	   custom report builder — reportId "new-custom" — all share this page)
	{
		path: '/reports2/time/:reportId',
		name: "reports2-time-report",
		component: () => import("/reports2/time-report.vue"),
		beforeEnter: requireTimeReport,
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Time Report" }
	},

	//---- Client-facing
	{
		path: '/reports2/client-time-breakdown',
		name: "reports2-client-time-breakdown",
		beforeEnter: requireReportsModuleAccess,
		component: () => import("/reports2/client-time-breakdown.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Client Time Breakdown" }
	},
	{
		path: '/reports2/customer-portal-preview',
		name: "reports2-customer-portal-preview",
		beforeEnter: requireUnreleasedReportsShown,
		component: () => import("/reports2/customer-portal-preview.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Customer Portal" }
	},

	//---- Sales & CRM. Reached from Sales & CRM > Reports (the Reports module's own menu isn't released yet), so the
	//	   breadcrumb leads back there.
	{
		path: '/reports2/sales/pipeline-by-stage',
		name: "reports2-sales-pipeline-by-stage",
		component: () => import("/reports2/sales/pipeline.vue"),
		props: { reportId: "RPT-STD-015", groupBy: "Stage" },
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Pipeline by Stage" }
	},
	{
		path: '/reports2/sales/pipeline-by-owner',
		name: "reports2-sales-pipeline-by-owner",
		component: () => import("/reports2/sales/pipeline.vue"),
		props: { reportId: "RPT-STD-016", groupBy: "Owner" },
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Pipeline by Owner" }
	},
	{
		path: '/reports2/sales/forecast',
		name: "reports2-sales-forecast",
		component: () => import("/reports2/sales/forecast.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Forecast by Quarter" }
	},
	{
		path: '/reports2/sales/target-attainment',
		name: "reports2-sales-target-attainment",
		component: () => import("/reports2/sales/target-attainment.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Target Attainment" }
	},
	{
		path: '/reports2/sales/win-loss',
		name: "reports2-sales-win-loss",
		component: () => import("/reports2/sales/win-loss.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Win / Loss" }
	},
	{
		path: '/reports2/sales/stalled-deals',
		name: "reports2-sales-stalled-deals",
		component: () => import("/reports2/sales/stalled-deals.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Aged & Stalled Deals" }
	},
	{
		path: '/reports2/sales/cost-of-acquisition',
		name: "reports2-sales-cost-of-acquisition",
		component: () => import("/reports2/sales/cost-of-acquisition.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Cost of Acquisition" }
	},
	{
		path: '/reports2/sales/activity',
		name: "reports2-sales-activity",
		component: () => import("/reports2/sales/activity.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "crm", parentRouteName: "crm-reports", pageTitle: "Sales & CRM > Reports > Activity & Touch Points" }
	},

	//---- Spend & budgets (phase 2)
	{
		path: '/reports2/bucket-spend',
		name: "reports2-bucket-spend",
		beforeEnter: requireUnreleasedReportsShown,
		component: () => import("/reports2/bucket-spend.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Bucket Spend" }
	},
	{
		path: '/reports2/margin-by-role',
		name: "reports2-margin-by-role",
		beforeEnter: requireUnreleasedReportsShown,
		component: () => import("/reports2/margin-by-role.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Margin by Role" }
	},

	//---- Workflow & hygiene
	{
		path: '/reports2/unsubmitted-time',
		name: "reports2-unsubmitted-time",
		beforeEnter: requireReportsModuleAccess,
		component: () => import("/reports2/unsubmitted-time.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Unsubmitted Time" }
	},
	{
		path: '/reports2/approval-status',
		name: "reports2-approval-status",
		beforeEnter: requireReportsModuleAccess,
		component: () => import("/reports2/approval-status.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Approval Status" }
	},
	{
		path: '/reports2/needs-allocation',
		name: "reports2-needs-allocation",
		beforeEnter: requireReportsModuleAccess,
		component: () => import("/reports2/needs-allocation.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "Needs Allocation" }
	},

	//---- Personal
	{
		path: '/reports2/my-time',
		name: "reports2-my-time",
		beforeEnter: requireReportsModuleAccess,
		component: () => import("/reports2/my-time.vue"),
		meta: { layout: "primary", layoutContainer: "none", module: "reports", parentRouteName: "reports2-index", pageTitle: "My Time" }
	},

]


export default routes;
